/**
 * AI API service for intelligent task features
 */

import { apiClient } from './client';

// Types
export interface TaskSuggestion {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimatedHours: number;
  suggestedLabels: string[];
  suggestedAssignee?: string;
  confidence: number;
}

export interface PrioritySuggestion {
  taskId: string;
  currentPriority: string;
  suggestedPriority: 'low' | 'medium' | 'high' | 'urgent';
  reasoning: string;
  confidence: number;
}

export interface SprintPlanSuggestion {
  taskIds: string[];
  totalPoints: number;
  estimatedCompletion: number;
  balanceScore: number;
  reasoning: string;
}

export interface NLPParseResult {
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  labels?: string[];
  assignee?: string;
  estimatedHours?: number;
}

export interface SimilarTask {
  taskId: string;
  title: string;
  similarity: number;
  status?: string;
  resolution?: string;
}

export interface WorkloadAnalysis {
  userId: string;
  displayName: string;
  currentTasks: number;
  totalPoints: number;
  estimatedHours: number;
  capacityPercentage: number;
  status: 'underloaded' | 'optimal' | 'overloaded';
}

export interface AIInsight {
  type: 'bottleneck' | 'risk' | 'opportunity' | 'trend';
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'critical';
  suggestedAction?: string;
  affectedItems?: string[];
}

export interface SummaryResult {
  summary: string;
  keyPoints: string[];
  sentiment: 'positive' | 'neutral' | 'negative';
}

// Natural Language Task Parsing Types
export interface ParsedTaskFull {
  title: string;
  description: string | null;
  assignee_hint: string | null;
  due_date: string | null;
  due_time: string | null;
  priority: string | null;
  labels: string[];
  estimated_hours: number | null;
  project_hint: string | null;
  recurrence_hint: string | null;
  confidence: number;
}

export interface ParseTaskResponse {
  parsed_task: ParsedTaskFull;
  original_input: string;
  suggestions: string[];
  warnings: string[];
  ai_used: boolean;
}

// Search Types
export interface SearchResult {
  entity_type: 'task' | 'comment' | 'meeting' | 'project';
  entity_id: string;
  title: string;
  snippet: string;
  relevance_score: number;
  metadata: Record<string, unknown>;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  total_count: number;
  took_ms: number;
  suggestions: string[];
  filters_applied: Record<string, unknown>;
  ai_interpretation: string | null;
}

export type SearchMode = 'semantic' | 'keyword' | 'hybrid';

export interface SearchParams {
  query: string;
  mode?: SearchMode;
  entity_types?: string[];
  filters?: Record<string, unknown>;
  limit?: number;
  offset?: number;
  use_ai_understanding?: boolean;
}

export interface CreateTaskFromNLResponse {
  preview: boolean;
  task_id?: string;
  title?: string;
  message?: string;
  parsed_task?: {
    title: string;
    description: string | null;
    due_date: string | null;
    priority: string | null;
    labels: string[];
  };
  confidence?: number;
  warnings?: string[];
  suggestions?: string[];
}

export interface EmbeddingStats {
  total: number;
  by_type: Record<string, number>;
}

// AI API functions
export const aiApi = {
  /**
   * Parse natural language input into structured task data
   */
  parseNaturalLanguage: async (input: string): Promise<NLPParseResult> => {
    const response = await apiClient.post<NLPParseResult>('/ai/parse', { input });
    return response.data;
  },

  /**
   * Get AI-powered task suggestions based on project context
   */
  getTaskSuggestions: async (projectId: string, context?: string): Promise<TaskSuggestion[]> => {
    const response = await apiClient.post<TaskSuggestion[]>('/ai/suggest-tasks', {
      projectId,
      context,
    });
    return response.data;
  },

  /**
   * Get priority suggestions for tasks in a project
   */
  getPrioritySuggestions: async (projectId: string): Promise<PrioritySuggestion[]> => {
    const response = await apiClient.get<PrioritySuggestion[]>(
      `/ai/priority-suggestions/${projectId}`
    );
    return response.data;
  },

  /**
   * Get sprint planning suggestions
   */
  getSprintPlanSuggestions: async (
    projectId: string,
    sprintCapacity: number
  ): Promise<SprintPlanSuggestion[]> => {
    const response = await apiClient.post<SprintPlanSuggestion[]>('/ai/sprint-plan', {
      projectId,
      sprintCapacity,
    });
    return response.data;
  },

  /**
   * Find similar tasks (for duplicate detection)
   */
  findSimilarTasks: async (
    projectId: string,
    title: string,
    description?: string
  ): Promise<SimilarTask[]> => {
    const response = await apiClient.post<SimilarTask[]>('/ai/similar-tasks', {
      projectId,
      title,
      description,
    });
    return response.data;
  },

  /**
   * Analyze team workload
   */
  analyzeWorkload: async (projectId: string): Promise<WorkloadAnalysis[]> => {
    const response = await apiClient.get<WorkloadAnalysis[]>(`/ai/workload/${projectId}`);
    return response.data;
  },

  /**
   * Get AI insights for a project
   */
  getProjectInsights: async (projectId: string): Promise<AIInsight[]> => {
    const response = await apiClient.get<AIInsight[]>(`/ai/insights/${projectId}`);
    return response.data;
  },

  /**
   * Summarize task comments or activity
   */
  summarizeActivity: async (taskId: string): Promise<SummaryResult> => {
    const response = await apiClient.get<SummaryResult>(`/ai/summarize/${taskId}`);
    return response.data;
  },

  /**
   * Auto-generate task description from title
   */
  generateDescription: async (title: string, context?: string): Promise<string> => {
    const response = await apiClient.post<{ description: string }>('/ai/generate-description', {
      title,
      context,
    });
    return response.data.description;
  },

  /**
   * Suggest labels for a task
   */
  suggestLabels: async (
    title: string,
    description?: string,
    existingLabels?: string[]
  ): Promise<string[]> => {
    const response = await apiClient.post<{ labels: string[] }>('/ai/suggest-labels', {
      title,
      description,
      existingLabels,
    });
    return response.data.labels;
  },

  /**
   * Estimate task complexity and time
   */
  estimateTask: async (
    title: string,
    description?: string
  ): Promise<{ points: number; hours: number; complexity: 'low' | 'medium' | 'high' }> => {
    const response = await apiClient.post<{
      points: number;
      hours: number;
      complexity: 'low' | 'medium' | 'high';
    }>('/ai/estimate', {
      title,
      description,
    });
    return response.data;
  },

  // ============================================================================
  // Natural Language Task Creation
  // ============================================================================

  /**
   * Parse natural language into structured task data (full AI parsing)
   */
  parseTaskFull: async (
    input: string,
    useAI: boolean = true,
    boardId?: string
  ): Promise<ParseTaskResponse> => {
    const response = await apiClient.post<ParseTaskResponse>('/ai/parse-task', {
      input,
      use_ai: useAI,
      board_id: boardId,
    });
    return response.data;
  },

  /**
   * Quick parse without AI (faster, less accurate)
   */
  quickParse: async (
    text: string
  ): Promise<{ title: string; due_date: string | null; priority: string | null; assignee_hint: string | null; confidence: number }> => {
    const response = await apiClient.post<{
      title: string;
      due_date: string | null;
      priority: string | null;
      assignee_hint: string | null;
      confidence: number;
    }>('/ai/quick-parse', { text });
    return response.data;
  },

  /**
   * Create a task directly from natural language
   */
  createTaskFromNL: async (
    input: string,
    boardId: string,
    confirm: boolean = false
  ): Promise<CreateTaskFromNLResponse> => {
    const response = await apiClient.post<CreateTaskFromNLResponse>(
      '/ai/create-task-from-nl',
      {
        input,
        board_id: boardId,
        confirm,
      }
    );
    return response.data;
  },

  // ============================================================================
  // Semantic Search
  // ============================================================================

  /**
   * Perform AI-powered semantic and keyword search
   */
  search: async (params: SearchParams): Promise<SearchResponse> => {
    const response = await apiClient.post<SearchResponse>('/ai/search', {
      query: params.query,
      mode: params.mode || 'hybrid',
      entity_types: params.entity_types,
      filters: params.filters,
      limit: params.limit || 20,
      offset: params.offset || 0,
      use_ai_understanding: params.use_ai_understanding ?? true,
    });
    return response.data;
  },

  /**
   * GET endpoint for search (convenient for URLs)
   */
  searchGet: async (
    query: string,
    mode: SearchMode = 'hybrid',
    types?: string[],
    limit: number = 20,
    offset: number = 0
  ): Promise<SearchResponse> => {
    const params = new URLSearchParams({
      q: query,
      mode,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (types?.length) {
      params.set('types', types.join(','));
    }

    const response = await apiClient.get<SearchResponse>(
      `/ai/search?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Conversational search with context from previous queries
   */
  conversationalSearch: async (
    query: string,
    conversationHistory?: Array<{ query: string; results_count?: number }>
  ): Promise<SearchResponse & { expanded_query?: string }> => {
    const response = await apiClient.post<SearchResponse & { expanded_query?: string }>(
      '/ai/search/conversational',
      { query, conversation_history: conversationHistory }
    );
    return response.data;
  },

  // ============================================================================
  // Embedding Management
  // ============================================================================

  /**
   * Get embedding storage statistics
   */
  getEmbeddingStats: async (): Promise<EmbeddingStats> => {
    const response = await apiClient.get<EmbeddingStats>('/ai/embeddings/stats');
    return response.data;
  },

  /**
   * Trigger reindex of embeddings
   */
  triggerReindex: async (entityType?: string): Promise<{ message: string; status: string }> => {
    const params = entityType ? `?entity_type=${entityType}` : '';
    const response = await apiClient.post<{ message: string; status: string }>(
      `/ai/embeddings/reindex${params}`
    );
    return response.data;
  },
};
